import { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import styles from './ImageAnnotator.module.css';
import generateBtnStyles from './generateBtn.module.css';
import deleteIcon from '../../assets/delete.png'
import deleteHoverIcon from '../../assets/delete-hover.png'
import newResizeIcon from '../../assets/newResize.png'
import { useTranslation } from 'react-i18next';
import { useTips } from '../../../../common/GlobalTips';
import animation_cursor from './assets/animation_cursor.png'
import resize0Cursor from './assets/resize-0.png';
import resize45Cursor from './assets/resize-45.png';
import resize90Cursor from './assets/resize-90.png';
import resize135Cursor from './assets/resize-135.png';
import addCursor from './assets/add.png';
import moveCursor from './assets/move.png';
import { isAnnotatorOpenAtom, isRodinGeneratingAtom, rodinInitWeightAtom, showSubscribeAtom, userPermissionAtom } from '../../../../store';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { getTargetDimensions, isHasAlpha, isHasAlpha2, rodinUploadCompressImg } from '../../../../utils/format';
import { rodinPreprocessImage } from '../../../../utils/net';
import { PermissionContext } from '../../../../utils/context';


const COLORS = [
  'rgba(246,121,59,1)',
  'rgba(137,59,246,1)',
  'rgba(59,130,246,1)',
  'rgba(246,59,181,1)',
  'rgba(59,246,246,1)'
];

const cursorImages = [addCursor, moveCursor, resize0Cursor, resize45Cursor, resize90Cursor, resize135Cursor];


/**
 * 鼠标移动的时候检测 activeBox & 边缘 or inside
 * 
 * 鼠标按下之后的操作在移动的时候决定
 * @param {} param0 
 * @returns 
 */

const ImageAnnotator = (props) => {
  const { initialData, isOpen, genrateAreaRef, inputAreaRef, uploadImageRaw } = props
  const isAnnotatorOpen = useRecoilValue(isAnnotatorOpenAtom);
  const tip = useTips();
  const [boxes, setBoxes] = useState(() => {

    if (!initialData?.bbox || !initialData?.dimensions) {
      tip({
        type: 'warning',
        content: "No object detection data found, please select it yourself.",
      });
      return []
    }

    const { width: img_width, height: img_height } = initialData.dimensions;
    const targetDims = getTargetDimensions(img_width, img_height);

    const scaleX = targetDims.width / img_width;
    const scaleY = targetDims.height / img_height;

    return initialData.bbox.slice(0, 5).map((box, index) => {
      const originPosition = {
        x: box[0],
        y: box[1],
        width: box[2],
        height: box[3]
      };

      // 使用缩放比例计算新位置
      const position = {
        x: originPosition.x * scaleX,
        y: originPosition.y * scaleY,
        width: originPosition.width * scaleX,
        height: originPosition.height * scaleY
      };

      return {
        id: `Object${index + 1}`,
        name: `Object${index + 1}`,
        color: COLORS[index % COLORS.length],
        position,
        originPosition
      };
    });
  });
  const [isNewMode, setIsNewMode] = useState(false);
  // const [activeBoxIdRef.current , setActiveBoxId] = useState(null);
  const activeBoxIdRef = useRef(null);
  const [newBox, setNewBox] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState(null);
  const resizeHandleRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const imageRef = useRef(null);

  // 添加蒙层透明度状态
  const [overlayOpacity, setOverlayOpacity] = useState(0);
  const animationRef = useRef(null);

  // 添加新建模式蒙版透明度状态
  const [newModeOverlayOpacity, setNewModeOverlayOpacity] = useState(0);
  const newModeAnimationRef = useRef(null);

  // 添加选框层级状态
  const [boxLayers, setBoxLayers] = useState(() =>
    initialData.bbox.map((_, index) => index)
  );


  const { t } = useTranslation();

  const StateMap = {
    'CONFIRM': t('BUTTON_CONFIRM'),
    'GENERATE': t('BUTTON_GENERATE'),
    'PREPARING': t('BUTTON_PREPARING'),
    'GENERATING': t('BUTTON_GENERATETING'),
    'UPLOADING': t('BUTTON_UPLOADING'),
    'QUEUING': t('BUTTON_QUEUING')
  }
  // 添加动画演示相关状态
  const [isShowingDemo, setIsShowingDemo] = useState(false);
  const [demoPosition, setDemoPosition] = useState({ x: 0, y: 0 });
  const [demoDragPosition, setDemoDragPosition] = useState(null);
  const demoAnimationRef = useRef(null);

  // 添加状态来控制示例框的透明度
  const [demoBoxOpacity, setDemoBoxOpacity] = useState(1);


  const getNextAvailableColor = useCallback((existingBoxes) => {
    const usedColors = new Set(existingBoxes.map(box => box.color));
    const availableColor = COLORS.find(color => !usedColors.has(color));
    return availableColor || COLORS[COLORS.length - 1];
  }, []);

  const setIsRodinGenerating = useSetRecoilState(isRodinGeneratingAtom)

  // 添加图片加载状态
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const canvasContainerRef = useRef(null)
  const canvasScrollRef = useRef(null)
  const generateBtnRef = useRef(null)
  const adjustingRef = useRef(null)
  const [activeBoxState, setActiveBoxState] = useState(null);
  const [showHorizontalScrollbar, setShowHorizontalScrollbar] = useState(false);
  const [showVerticalScrollbar, setShowVerticalScrollbar] = useState(false);

  const velocityRef = useRef(0);
  const animationFrameRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [useVerticalLayout, setUseVerticalLayout] = useState(false);
  const setShowSubscribe = useSetRecoilState(showSubscribeAtom);
  const userPermission = useRecoilValue(userPermissionAtom);
  const [multiImgTipBox, setMultiImgTipBox] = useState(false)
  const [currentStatus, setCurrentStatus] = useState(isAnnotatorOpen?.isCrop ? 'CONFIRM' : 'GENERATE')
  const [fullImageGenerating, setFullImageGenerating] = useState(false)
  const { isCreatorOrAbove } = useContext(PermissionContext);

  // 修改按钮点击处理
  const handleNewClick = () => {
    if (!isDragging) {
      // 如果当前在新建模式，点击 Cancel 时清除相关状态
      if (isNewMode) {
        setIsNewMode(false);
        setNewBox(null);
        setIsShowingDemo(false);
        setDemoPosition(null);
        setDemoDragPosition(null);
        if (demoAnimationRef.current) {
          cancelAnimationFrame(demoAnimationRef.current);
        }
        return;
      }

      // 检查是否达到最大框数限制
      if (boxes.length >= 5) {
        tip({
          type: 'warning',
          content: "You can only create up to 5 annotation boxes.",
        });
        return;
      }

      // 只有在首次进入新建模式时才显示演示动画
      setIsNewMode(true);
      if (!isShowingDemo) {  // 添加这个条件检查
        setIsShowingDemo(true);
        startDemoAnimation();
      }
    }
  };

  // 修改 startDemoAnimation 函数，确保动画结束后正确清理状态
  const startDemoAnimation = useCallback(() => {
    const runDemo = () => {
      const startX = canvasRef.current.width * 0.3;
      const startY = canvasRef.current.height * 0.3;
      const endX = canvasRef.current.width * 0.7;
      const endY = canvasRef.current.height * 0.7;

      let progress = 0;

      const animate = () => {
        progress += 0.01;

        if (progress <= 1.3) {
          if (progress < 0.3) {
            // 第一阶段：从左上到右下，绘制框
            const ratio = progress / 0.3;
            setDemoPosition({
              x: startX + (endX - startX) * ratio,
              y: startY + (endY - startY) * ratio
            });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(1);
          } else if (progress < 0.5) {
            // 第二阶段：保持框显示一会
            setDemoPosition({ x: endX, y: endY });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(1);
          } else if (progress < 0.7) {
            // 第三阶段：鼠标回到左上，框逐渐消失
            const ratio = (progress - 0.5) / 0.2;
            setDemoPosition({
              x: endX - (endX - startX) * ratio,
              y: endY - (endY - startY) * ratio
            });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(1 - ratio);
          } else if (progress < 0.8) {
            // 第四阶段：保持在左上一会
            setDemoPosition({ x: startX, y: startY });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(0);
          } else if (progress < 1.0) {
            // 第五阶段：再次从左上到右下，绘制框
            const ratio = (progress - 0.8) / 0.2;
            setDemoPosition({
              x: startX + (endX - startX) * ratio,
              y: startY + (endY - startY) * ratio
            });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(ratio);
          } else if (progress < 1.2) {
            // 新增第六阶段：在右下角停留
            setDemoPosition({ x: endX, y: endY });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(1);
          } else {
            // 第七阶段：框淡出消失
            const ratio = (progress - 1.2) / 0.1;
            setDemoPosition({ x: endX, y: endY });
            setDemoDragPosition({ x: startX, y: startY });
            setDemoBoxOpacity(1 - ratio);
          }
          demoAnimationRef.current = requestAnimationFrame(animate);
        } else {
          // 动画结束时清理状态，但保持新建模式
          setIsShowingDemo(false);
          setDemoPosition(null);
          setDemoDragPosition(null);
          setDemoBoxOpacity(0);
          if (demoAnimationRef.current) {
            cancelAnimationFrame(demoAnimationRef.current);
            demoAnimationRef.current = null;
          }
        }
      };

      demoAnimationRef.current = requestAnimationFrame(animate);
    };

    runDemo();
  }, []);

  // 处理蒙层动画
  useEffect(() => {
    if (activeBoxIdRef.current) {
      // 如果当前透明度已经接近目标值，不需要动画
      if (overlayOpacity < 0.45) {
        let opacity = overlayOpacity; // 从当前透明度开始，而不是从0开始
        const animate = () => {
          opacity += 0.05;
          setOverlayOpacity(Math.min(opacity, 0.5));

          if (opacity < 0.5) {
            animationRef.current = requestAnimationFrame(animate);
          }
        };
        animationRef.current = requestAnimationFrame(animate);
      }
    } else {
      // 只有在真正需要淡出时才执行淡出动画
      if (overlayOpacity > 0.05) {
        let opacity = overlayOpacity;
        const animate = () => {
          opacity -= 0.05;
          setOverlayOpacity(Math.max(opacity, 0));

          if (opacity > 0) {
            animationRef.current = requestAnimationFrame(animate);
          }
        };
        animationRef.current = requestAnimationFrame(animate);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [activeBoxIdRef.current, overlayOpacity]);


  // 添加新建模式蒙版动画效果
  useEffect(() => {
    if (isNewMode) {
      let opacity = 0;
      const animate = () => {
        opacity += 0.02;
        setNewModeOverlayOpacity(Math.min(opacity, 0.5));

        if (opacity < 0.5) {
          newModeAnimationRef.current = requestAnimationFrame(animate);
        }
      };
      newModeAnimationRef.current = requestAnimationFrame(animate);
    } else {
      let opacity = newModeOverlayOpacity;
      const animate = () => {
        opacity -= 1;
        setNewModeOverlayOpacity(Math.max(opacity, 0));

        if (opacity > 0) {
          newModeAnimationRef.current = requestAnimationFrame(animate);
        }
      };
      newModeAnimationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (newModeAnimationRef.current) {
        cancelAnimationFrame(newModeAnimationRef.current);
      }
    };
  }, [isNewMode]);

  // 修改检测函数,添加阈值参数
  const isPointInBox = (x, y, box, threshold = 8) => {
    const { position } = box;
    return x >= position.x - threshold &&
      x <= position.x + position.width + threshold &&
      y >= position.y - threshold &&
      y <= position.y + position.height + threshold;
  };

  const drawBox = (box, isActive) => {
    if (!ctxRef.current) return;
    const ctx = ctxRef.current;
    const { position, color } = box;
    const lineWidth = 2 * devicePixelRatio;

    // 绘制框
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.rect(
      position.x * devicePixelRatio,
      position.y * devicePixelRatio,
      position.width * devicePixelRatio,
      position.height * devicePixelRatio
    );
    ctx.stroke();
  }

  // 修改 getResizeHandle 函数，为角点和边缘设置同的检测范围
  const getResizeHandle = (x, y, box) => {
    if (!box || !box.position) return null;

    const { position } = box;
    const cornerSize = 10 * devicePixelRatio;  // 角点检测范围更大
    const edgeSize = 8 * devicePixelRatio;   // 边缘检测范围较小

    // 四个角的调大点 (使用更大的检测范围)
    const corners = [
      { type: 'topLeft', x: position.x, y: position.y },
      { type: 'topRight', x: position.x + position.width, y: position.y },
      { type: 'bottomLeft', x: position.x, y: position.y + position.height },
      { type: 'bottomRight', x: position.x + position.width, y: position.y + position.height }
    ];

    // 四条边的调整区域 (使用较小的检测范围)
    const edges = [
      { type: 'top', x: position.x + position.width / 2, y: position.y },
      { type: 'right', x: position.x + position.width, y: position.y + position.height / 2 },
      { type: 'bottom', x: position.x + position.width / 2, y: position.y + position.height },
      { type: 'left', x: position.x, y: position.y + position.height / 2 }
    ];

    // 先检查是否在角点上（优先级更高）
    for (const handle of corners) {
      if (Math.abs(x - handle.x) <= cornerSize && Math.abs(y - handle.y) <= cornerSize) {
        return handle.type;
      }
    }

    // 再检查是否在边上
    for (const edge of edges) {
      if (edge.type === 'left' || edge.type === 'right') {
        if (Math.abs(x - edge.x) <= edgeSize &&
          y >= position.y + cornerSize &&  // 避免与角点检测区域重叠
          y <= position.y + position.height - cornerSize) {
          return edge.type;
        }
      } else {
        if (Math.abs(y - edge.y) <= edgeSize &&
          x >= position.x + cornerSize &&  // 避免与角点检测区域重叠
          x <= position.x + position.width - cornerSize) {
          return edge.type;
        }
      }
    }

    return null;
  };

  const getEdgeCursor = (x, y) => {
    const threshold = 8;
    const px = x / devicePixelRatio;
    const py = y / devicePixelRatio;

    // 1. 首先检查激活框的边缘
    if (activeBoxIdRef.current) {
      const activeBox = boxes.find(box => box.id === activeBoxIdRef.current);
      if (activeBox) {
        const { position } = activeBox;
        const isNearLeft = Math.abs(px - position.x) <= threshold;
        const isNearRight = Math.abs(px - (position.x + position.width)) <= threshold;
        const isNearTop = Math.abs(py - position.y) <= threshold;
        const isNearBottom = Math.abs(py - (position.y + position.height)) <= threshold;

        // 如果在激活框的边缘，直接返回resize光标
        if (isNearLeft || isNearRight || isNearTop || isNearBottom) {
          if ((isNearLeft && isNearTop) || (isNearRight && isNearBottom)) {
            return { type: 'resize', angle: 45 };
          }
          if ((isNearRight && isNearTop) || (isNearLeft && isNearBottom)) {
            return { type: 'resize', angle: 135 };
          }
          if (isNearTop || isNearBottom) {
            return { type: 'resize', angle: 90 };
          }
          if (isNearLeft || isNearRight) {
            return { type: 'resize', angle: 0 };
          }
        }

        // 如果在激活框内部但不在边缘，返回move光标
        if (isPointInBox(px, py, activeBox)) {
          return { type: 'move' };
        }
      }
    }

    // 2. 然后检查其他框
    const anyBox = boxes.find(box =>
      box.id !== activeBoxIdRef.current && isPointInBox(px, py, box)
    );
    if (anyBox) {
      return { type: 'move' };
    }

    // 3. 如果都不满足，返回add光标
    return { type: 'add' };
  };

  const handleCanvasMouseMove = (e) => {
    if (isDragging) return;

    const rect = canvasRef.current.getBoundingClientRect();
    let currentX = (e.clientX - rect.left) * devicePixelRatio;
    let currentY = (e.clientY - rect.top) * devicePixelRatio;

    currentX = Math.max(0, Math.min(currentX, canvasRef.current.width));
    currentY = Math.max(0, Math.min(currentY, canvasRef.current.height));

    const cursor = getEdgeCursor(currentX, currentY);
    const sortedBoxes = [...boxes].sort((a, b) => {
      const indexA = boxLayers.indexOf(boxes.findIndex(box => box.id === a.id));
      const indexB = boxLayers.indexOf(boxes.findIndex(box => box.id === b.id));
      return indexB - indexA;
    });

    // 先重置鼠标样式为默认
    // canvasRef.current.style.cursor = `url(${addCursor}) 16 16, auto`;

    // 使用定时器延迟处理hover效果
    let isInAnyBox = false;

    // 遍历所有框，检查鼠标是否在框内
    for (const box of sortedBoxes) {
      if (isPointInBox(currentX / devicePixelRatio, currentY / devicePixelRatio, box)) {
        isInAnyBox = true;
        if (box.id !== activeBoxIdRef.current) {
          setBoxLayers(prev => {
            const boxIndex = boxes.findIndex(b => b.id === box.id);
            const newLayers = prev.filter(layer => layer !== boxIndex);
            return [...newLayers, boxIndex];
          });
        }
        adjustingRef.current = true;
        activeBoxIdRef.current = box.id;
        break;
      }
    }

    if (!isInAnyBox) {
      adjustingRef.current = false;
      activeBoxIdRef.current = null;
      setActiveBoxState(null);
    } else {
      setActiveBoxState(activeBoxIdRef.current);
    }

    // 更新鼠标样式
    switch (cursor.type) {
      case 'move':
        canvasRef.current.style.cursor = `url(${moveCursor}) 16 16, auto`;
        break;
      case 'resize': {
        const cursorMap = {
          0: resize0Cursor,
          45: resize45Cursor,
          90: resize90Cursor,
          135: resize135Cursor
        };
        canvasRef.current.style.cursor = `url(${cursorMap[cursor.angle]}) 16 16, auto`;
        break;
      }
      case 'add':
        canvasRef.current.style.cursor = `url(${addCursor}) 16 16, auto`;
        break;
      default:
        canvasRef.current.style.cursor = `url(${addCursor}) 16 16, auto`;
    }
  };


  // 修改 handleMouseMove，确保拖动时实时显示新框
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const scaleFactor = getScaleFactor();
    let currentX = (e.clientX - rect.left) * devicePixelRatio / scaleFactor;
    let currentY = (e.clientY - rect.top) * devicePixelRatio / scaleFactor;

    // 确保 currentX 和 currentY 在画布范围内
    currentX = Math.max(0, Math.min(currentX, canvasRef.current.width));
    currentY = Math.max(0, Math.min(currentY, canvasRef.current.height));

    if (newBox) {
      // 新建框的处理：直接根据拖动计算大小
      const width = (currentX - dragStartPos.x) / devicePixelRatio;
      const height = (currentY - dragStartPos.y) / devicePixelRatio;

      const newPosition = {
        x: (width < 0 ? currentX : dragStartPos.x) / devicePixelRatio,
        y: (height < 0 ? currentY : dragStartPos.y) / devicePixelRatio,
        width: Math.abs(width),
        height: Math.abs(height)
      };

      setNewBox(prev => ({
        ...prev,
        position: checkBoundaries(newPosition, true)
      }));
      return; // 在新建模式下，不处理其他逻辑
    }

    // 只有在非新建模式下才处理已有框的移动和调整
    if (activeBoxIdRef.current && !isNewMode) {
      setBoxes(prev => prev.map(box => {
        if (box.id === activeBoxIdRef.current) {
          const deltaX = (currentX - dragStartPos.x) / devicePixelRatio;
          const deltaY = (currentY - dragStartPos.y) / devicePixelRatio;
          let newPosition = { ...box.position };

          if (resizeHandleRef.current) {
            switch (resizeHandleRef.current) {
              case 'topLeft':
                newPosition = {
                  x: Math.min(box.position.x + deltaX, box.position.x + box.position.width - 10),
                  y: Math.min(box.position.y + deltaY, box.position.y + box.position.height - 10),
                  width: Math.max(10, box.position.width - deltaX),
                  height: Math.max(10, box.position.height - deltaY)
                };
                break;
              case 'topRight':
                newPosition = {
                  x: box.position.x,
                  y: Math.min(box.position.y + deltaY, box.position.y + box.position.height - 10),
                  width: Math.max(10, box.position.width + deltaX),
                  height: Math.max(10, box.position.height - deltaY)
                };
                break;
              case 'bottomLeft':
                newPosition = {
                  x: Math.min(box.position.x + deltaX, box.position.x + box.position.width - 10),
                  y: box.position.y,
                  width: Math.max(10, box.position.width - deltaX),
                  height: Math.max(10, box.position.height + deltaY)
                };
                break;
              case 'bottomRight':
                newPosition = {
                  x: box.position.x,
                  y: box.position.y,
                  width: Math.max(10, box.position.width + deltaX),
                  height: Math.max(10, box.position.height + deltaY)
                };
                break;
              case 'top':
                newPosition = {
                  x: box.position.x,
                  y: Math.min(box.position.y + deltaY, box.position.y + box.position.height - 10),
                  width: box.position.width,
                  height: Math.max(10, box.position.height - deltaY)
                };
                break;
              case 'right':
                newPosition = {
                  x: box.position.x,
                  y: box.position.y,
                  width: Math.max(10, box.position.width + deltaX),
                  height: box.position.height
                };
                break;
              case 'bottom':
                newPosition = {
                  x: box.position.x,
                  y: box.position.y,
                  width: box.position.width,
                  height: Math.max(10, box.position.height + deltaY)
                };
                break;
              case 'left':
                newPosition = {
                  x: Math.min(box.position.x + deltaX, box.position.x + box.position.width - 10),
                  y: box.position.y,
                  width: Math.max(10, box.position.width - deltaX),
                  height: box.position.height
                };
                break;
            }

            // 确保调整大小时不移动框
            newPosition = checkBoundaries(newPosition, true);
          } else {
            newPosition = {
              x: box.position.x + deltaX,
              y: box.position.y + deltaY,
              width: box.position.width,
              height: box.position.height
            };
            newPosition = checkBoundaries(newPosition, false);
          }
          // adjustingRef.current = true
          return { ...box, position: newPosition };
        }
        return box;
      }));
    }

    setDragStartPos({ x: currentX, y: currentY });
  }, [isDragging, activeBoxIdRef.current, dragStartPos, resizeHandleRef.current, newBox, isNewMode, getEdgeCursor]);

  const drawAll = () => {

    if (!ctxRef.current || !imageRef.current || !isImageLoaded) return;
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;

    // 清空画布并绘制图片
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);

    // 按层级顺序绘制非激活框
    const sortedBoxIndices = [...boxLayers].filter(index => index < boxes.length);
    sortedBoxIndices.forEach(boxIndex => {
      const box = boxes[boxIndex];
      if (box && box.id !== activeBoxIdRef.current) {
        drawBox(box, false);
      }
    });

    // 绘制蒙层和激活框
    if (activeBoxIdRef.current) {
      const activeBox = boxes.find(box => box.id === activeBoxIdRef.current);
      if (activeBox) {
        // 绘制蒙层
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, canvas.height);
        ctx.rect(
          activeBox.position.x * devicePixelRatio,
          activeBox.position.y * devicePixelRatio,
          activeBox.position.width * devicePixelRatio,
          activeBox.position.height * devicePixelRatio
        );
        ctx.clip('evenodd');
        ctx.fillStyle = `rgba(0, 0, 0, ${overlayOpacity})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        // 绘制激活框
        drawBox(activeBox, true);
      }
    }
    // 修改新建模式蒙版绘制逻辑
    if (isNewMode) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);

      // 如果正在绘制新框，则将新框区域裁剪掉
      if (newBox) {
        ctx.rect(
          newBox.position.x * devicePixelRatio,
          newBox.position.y * devicePixelRatio,
          newBox.position.width * devicePixelRatio,
          newBox.position.height * devicePixelRatio
        );
      }

      ctx.clip('evenodd');
      ctx.fillStyle = `rgba(0, 0, 0, ${newModeOverlayOpacity})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }

    // 如果正在绘制新框，绘制新框
    if (newBox) {
      drawBox(newBox, true);
    }

    // 绘制演示动画
    if (isShowingDemo && demoPosition) {
      // 制鼠标光标图片
      const cursorImage = new Image();
      cursorImage.src = animation_cursor;

      // 使用固定高度，宽度自适应
      const cursorHeight = 24 * devicePixelRatio;
      const aspectRatio = cursorImage.width / cursorImage.height;
      const cursorWidth = cursorHeight * aspectRatio;

      ctx.drawImage(
        cursorImage,
        demoPosition.x - cursorWidth / 2,
        demoPosition.y - cursorHeight / 2,
        cursorWidth,
        cursorHeight
      );

      // 如果正在拖动,绘制演示框
      if (demoDragPosition) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(246,121,59,${demoBoxOpacity})`; // 使用带透明度的颜色
        ctx.lineWidth = 2 * devicePixelRatio;
        ctx.rect(
          demoDragPosition.x,
          demoDragPosition.y,
          demoPosition.x - demoDragPosition.x,
          demoPosition.y - demoDragPosition.y
        );
        ctx.stroke();
      }
    }
  }

  const handleMouseDown = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) * devicePixelRatio;
    const y = (e.clientY - rect.top) * devicePixelRatio;

    // 检查是否在任何已有框内(包括扩展区域)
    const isInAnyBox = boxes.some(box =>
      isPointInBox(x / devicePixelRatio, y / devicePixelRatio, box, 8)
    );

    // 如果在新建模式或者点击位置不在任何框内且未达到最大框数，允许创建新框
    if ((isNewMode || !isInAnyBox)) {
      if (boxes.length >= 5) {
        tip({
          type: 'warning',
          content: "You can only create up to 5 annotation boxes.",
        });
        return;
      } else {
        // 找到当前最大的 Object 编号
        const maxObjectNum = boxes.reduce((max, box) => {
          const match = box.name.match(/Object(\d+)/);
          if (match) {
            const num = parseInt(match[1]);
            return Math.max(max, num);
          }
          return max;
        }, 0);

        const newBoxData = {
          id: `Object${maxObjectNum + 1}`,
          name: `Object${maxObjectNum + 1}`,
          color: getNextAvailableColor(boxes),
          position: {
            x: x / devicePixelRatio,
            y: y / devicePixelRatio,
            width: 0,
            height: 0
          }
        };
        setNewBox(newBoxData);
        // setActiveBoxId(null);
        // activeBoxIdRef.current = null;
        setDragStartPos({ x, y });
        setIsDragging(true);
        setIsNewMode(true);
        return;
      }
    }

    if (!isNewMode) {
      // 1. 首先检查当前激活框的调整点
      if (activeBoxIdRef.current) {
        const activeBox = boxes.find(box => box.id === activeBoxIdRef.current);
        const handle = getResizeHandle(x / devicePixelRatio, y / devicePixelRatio, activeBox);
        if (handle) {
          resizeHandleRef.current = handle;
          setDragStartPos({ x, y });
          setIsDragging(true);
          return;
        }
      }

      // 2. 检查所有框，按照层级从上到下遍历
      const sortedBoxes = [...boxes].sort((a, b) => {
        const indexA = boxLayers.indexOf(boxes.findIndex(box => box.id === a.id));
        const indexB = boxLayers.indexOf(boxes.findIndex(box => box.id === b.id));
        return indexB - indexA;
      });

      for (const box of sortedBoxes) {
        if (isPointInBox(x / devicePixelRatio, y / devicePixelRatio, box)) {
          if (box.id !== activeBoxIdRef.current) {
            setBoxLayers(prev => {
              const boxIndex = boxes.findIndex(b => b.id === box.id);
              const newLayers = prev.filter(layer => layer !== boxIndex);
              return [...newLayers, boxIndex];
            });
          }

          // activeBoxIdRef.current = box.id;
          setDragStartPos({ x, y });
          setIsDragging(true);
          return;
        }
      }

      // 点击空白区域
      // activeBoxIdRef.current = null;
      setDragStartPos({ x, y });
      setIsDragging(true);
    }
  }, [isNewMode, boxes, activeBoxIdRef.current, boxLayers, getNextAvailableColor]);

  // 修改边界检测函数，分别处理移动和调整大小的情况
  const checkBoundaries = (position, isResize = false) => {
    if (!canvasRef.current) return position;

    const canvas = canvasRef.current;
    const maxX = canvas.width / devicePixelRatio;
    const maxY = canvas.height / devicePixelRatio;

    if (isResize) {
      // 调整大小时的边界检测
      return {
        x: Math.max(0, position.x),
        y: Math.max(0, position.y),
        width: Math.max(10, Math.min(position.width, maxX - position.x)),
        height: Math.max(10, Math.min(position.height, maxY - position.y))
      };
    } else {
      // 移动时的边界检测，保持原有大小
      return {
        x: Math.max(0, Math.min(position.x, maxX - position.width)),
        y: Math.max(0, Math.min(position.y, maxY - position.height)),
        width: position.width,
        height: position.height
      };
    }
  };

  const handleMouseUp = useCallback((e) => {
    if (newBox) {
      if (newBox.position.width > 10 && newBox.position.height > 10) {
        setBoxes(prev => [...prev, newBox]);
        // 将新框添加到最顶层
        setBoxLayers(prev => [...prev, boxes.length]);
      }
      setNewBox(null);
    }
    if (adjustingRef.current) {
      // activeBoxIdRef.current = null;
      // adjustingRef.current = false
    }

    setIsDragging(false);
    resizeHandleRef.current = null;
    setIsNewMode(false);
  }, [newBox, boxes.length]);


  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctxRef.current = ctx;

    const updateCanvasSize = () => {
      const container = canvas.parentElement;
      if (!container || !imageRef.current || !isImageLoaded) return;

      const dimensions = getTargetDimensions(imageRef.current.naturalWidth, imageRef.current.naturalHeight);
      setUseVerticalLayout(dimensions.shouldUseVerticalLayout);

      // 设置容器尺寸
      canvasRef.current.style.width = `${dimensions.containerWidth}px`;
      canvasRef.current.style.height = `${dimensions.containerHeight}px`;

      // 设置canvas实际尺寸
      canvasRef.current.width = dimensions.width * devicePixelRatio;
      canvasRef.current.height = dimensions.height * devicePixelRatio;
      canvas.style.width = `${dimensions.width}px`;
      canvas.style.height = `${dimensions.height}px`;

      // 修改定位方式
      canvas.style.position = 'relative'; // 改为相对定位
      canvas.style.left = '0';
      canvas.style.top = '0';
      canvas.style.transform = 'none';

      drawAll();
    };

    // 添加 ResizeObserver 监听容器大小变化
    const resizeObserver = new ResizeObserver(() => {
      if (isImageLoaded) {
        updateCanvasSize();
      }
    });

    resizeObserver.observe(document.body);

    // 如果图片已加载，立即更新画布大小
    if (isImageLoaded) {
      updateCanvasSize();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen, drawAll, isImageLoaded]);

  // 修改图片加载逻辑
  useEffect(() => {
    if (!isOpen || !initialData.imageFile) {
      setIsImageLoaded(false);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(initialData.imageFile);

    img.onload = () => {
      imageRef.current = img;
      setIsImageLoaded(true);
    };

    img.onerror = () => {
      console.error('Failed to load image');
      setIsImageLoaded(false);
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;

    return () => {
      URL.revokeObjectURL(objectUrl);
      if (imageRef.current) {
        imageRef.current = null;
      }
      setIsImageLoaded(false);
    };
  }, [isOpen, initialData.imageFile]);

  useEffect(() => {
    if (!isOpen) return; // 如果不开启，直接返回
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove, handleMouseUp, isOpen]);

  // 当boxes或activeBoxId改变时重新绘制
  useEffect(() => {
    if (!ctxRef.current || !imageRef.current) return;
    drawAll();
  }, [boxes, activeBoxIdRef.current, adjustingRef.current, activeBoxState, drawAll, ctxRef.current, imageRef.current]);


  const handleDeleteBox = useCallback((boxId) => {
    // 清除当前激活的框
    if (activeBoxIdRef.current === boxId) {
      activeBoxIdRef.current = null;
    }

    // 更新 boxes
    setBoxes(prev => {
      const newBoxes = prev.filter(b => b.id !== boxId);
      return newBoxes;
    });

    // 更新 boxLayers
    setBoxLayers(prev => {
      const boxIndex = boxes.findIndex(b => b.id === boxId);
      const newLayers = prev
        .filter(layer => layer !== boxIndex)
        .map(layer => layer > boxIndex ? layer - 1 : layer);
      return newLayers;
    });
  }, [activeBoxIdRef.current, boxes]);

  // 清理动画
  useEffect(() => {
    const preloadImages = cursorImages.map(src => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = src;
      });
    });

    Promise.all(preloadImages);
    return () => {
      if (demoAnimationRef.current) {
        cancelAnimationFrame(demoAnimationRef.current);
      }
    };
  }, []);

  const handleClose = (e) => {
    if ((canvasContainerRef.current && canvasContainerRef.current.contains(e.target)) || (generateBtnRef.current && generateBtnRef.current.contains(e.target))) {
      return
    }
    if (isGenerating) {
      tip({
        type: 'warning',
        content: "Generating, please wait...",
      })
      return
    }
    tip({
      type: 'primary',
      content: t('TIP_PRIMARY_APP_MODEL_CLOSE'),
      yes: () => genrateAreaRef.current.closeAnnotator(),
      no: (f) => f,
    });
  }

  const handleMouseEnterBtn = (box) => {
    if (isGenerating) return
    if (!isDragging) {
      activeBoxIdRef.current = box.id
      setActiveBoxState(box.id)
      scrollToOptimalView(box.id);
    }
  }

  const handleMouseLeaveBtn = () => {
    if (isGenerating) return
    if (!isDragging) {
      activeBoxIdRef.current = null
      setActiveBoxState(null)
    }
  }

  useEffect(() => {
    if (!canvasScrollRef.current) return;

    const container = canvasScrollRef.current;
    setShowHorizontalScrollbar(container.scrollWidth > container.clientWidth);
    setShowVerticalScrollbar(container.scrollHeight > container.clientHeight);
  }, [isImageLoaded]);


  useEffect(() => {
    if (!canvasScrollRef.current) return;
    const container = canvasScrollRef.current;

    const handleWheel = (e) => {
      if (showHorizontalScrollbar) {
        e.preventDefault();

        // 更新速度，加入一个系数使滚动更平滑
        const speedFactor = 0.3; // 可以调整这个值来改变滚动的敏感度
        velocityRef.current += e.deltaY * speedFactor;

        // 如果没有动画在运行，启动动画
        if (!animationFrameRef.current) {
          const animate = () => {
            if (Math.abs(velocityRef.current) < 0.1) {
              velocityRef.current = 0;
              animationFrameRef.current = null;
              return;
            }

            // 更新滚动位置
            container.scrollLeft += velocityRef.current;

            // 应用摩擦力，使速度逐渐减小
            velocityRef.current *= 0.95; // 可以调整这个值来改变减速的快慢

            animationFrameRef.current = requestAnimationFrame(animate);
          };

          animationFrameRef.current = requestAnimationFrame(animate);
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [showHorizontalScrollbar]);

  const scrollToOptimalView = useCallback((boxId) => {
    if (!canvasScrollRef.current || !boxes.length) return;

    const box = boxes.find(b => b.id === boxId);
    if (!box) return;

    const container = canvasScrollRef.current;
    const boxPosition = box.position;

    // 计算框的实际位置和尺寸
    const boxLeft = boxPosition.x;
    const boxTop = boxPosition.y;

    // 计算当前可视区域
    const viewportWidth = container.clientWidth;
    const viewportHeight = container.clientHeight;

    // 计算最佳滚动位置
    let targetScrollLeft;
    let targetScrollTop;

    // 水平方向的处理
    if (boxPosition.width <= viewportWidth) {
      // 如果框的宽度小于视口，将其居中
      targetScrollLeft = boxLeft - (viewportWidth - boxPosition.width) / 2;
    } else {
      // 如果框的宽度大于视口，尽可能展示更多
      targetScrollLeft = boxLeft;
    }

    // 垂直方向的处理
    if (boxPosition.height <= viewportHeight) {
      // 如果框的高度小于视口，将其居中
      targetScrollTop = boxTop - (viewportHeight - boxPosition.height) / 2;
    } else {
      // 如果框的高度大于视口，尽可能展示更多
      targetScrollTop = boxTop;
    }

    // 确保滚动位置在有效范围内
    targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, container.scrollWidth - viewportWidth));
    targetScrollTop = Math.max(0, Math.min(targetScrollTop, container.scrollHeight - viewportHeight));

    // 使用平滑滚动效果
    container.scrollTo({
      left: targetScrollLeft,
      top: targetScrollTop,
      behavior: 'smooth'
    });
  }, [boxes]);

  const handleOpenSub = () => {
    genrateAreaRef.current.closeAnnotator()
    setShowSubscribe(true)
    setMultiImgTipBox(false)
  }

  const handlerConfirmGenerate = async () => {
    if (boxes.length === 0) {
      tip({
        content: "Please select at least one object.",
        type: "warning"
      })
      return
    }
    if (boxes.length === 1) {
      //走单图生成  
      handlerClickWeight(1)
    } else {
      // 非订阅用户
      if (!isCreatorOrAbove()) {
        setMultiImgTipBox(true)
        setTimeout(() => {
          setMultiImgTipBox(false)
        }, 5000);
        return
      }
      setCurrentStatus('MULTI')
    }
  }

  const handlerConfirmCrop = async () => {
    if (boxes.length === 0) {
      tip({
        type: 'warning',
        content: "Please select at least one object.",
      });
      return
    }

    if (uploadImageRaw.length + boxes.length > 5) {
      tip({
        type: 'warning',
        content: t('TIP_WRAN_NEWRODIN_IMG_LIMIT'),
      });
      return;
    }
    const croppedImages = await cropAndDownloadBoxes();
    const originalImgName = isAnnotatorOpen.name
    genrateAreaRef.current.closeAnnotator()
    inputAreaRef?.current?.handleCropFiles(croppedImages, originalImgName)
  }

  const handlerGenerate = () => {
    const eventMap = {
      'CONFIRM': handlerConfirmCrop,
      'GENERATE': handlerConfirmGenerate
    }
    const handler = eventMap[currentStatus];
    if (handler) {
      handler();
    } else {
      return
    }
  }

  useEffect(() => {
    if (boxes.length <= 1 && currentStatus === 'MULTI') {
      // 处理不讲武德的用户
      setCurrentStatus('GENERATE')
    }
  }, [boxes])

  /**
* 图片生成流程
* @param {number} index - 权重索引（0: concat, 1: fuse）
* 
* 主要步骤：
* 1. 从 Canvas 裁剪出标注框内的图片
* 2. 进行两次 Alpha 通道检测
*    - 第一次：检测初始裁剪图片
*    - 第二次：检测压缩后的图片
* 3. 图片压缩和预处理
*    - 压缩原始图片
*    - 预处理并生成新的图片文件
* 4. 提取第一张图片的特殊属性（prompt, height等）
* 5. 开始生成流程
* 
* 注：整个过程会维护一个 imagesData 数组，
* 存储每张图片在不同阶段的处理结果
*/

  const handlerClickWeight = async (index, e) => {
    e?.stopPropagation()
    setIsGenerating(true);

    // 1. 处理图片裁剪和alpha检测
    setCurrentStatus("GENERATING")
    const condition_mode = ['concat', 'fuse'][index]
    const croppedImages = await cropAndDownloadBoxes();
    const alphaResults = await Promise.all(croppedImages.map(isHasAlpha2));
    // 2. 构建图片数据结构
    const imagesData = croppedImages.map((file, index) => ({
      originFile: file,
      name: alphaResults[index].file,
      isHasAlpha: alphaResults[index].isHasAlpha
    }));

    // 3. 压缩图片
    const { fileData } = await rodinUploadCompressImg(croppedImages, alphaResults);
    imagesData.forEach((item, index) => {
      item.compressedfile = fileData[index].file;
      item.compressedBase64 = fileData[index].data;
    });

    const result = await Promise.all(imagesData.map(async (item) => {
      return await isHasAlpha(item.compressedfile)
    }))

    imagesData.forEach((item, index) => {
      item.combineFile = result[index].data;
      item.combineDataBase64 = result[index].dataUrl;
    });

    // 4. 预处理图片并获取第一张图片的特殊属性
    const preprocessResults = await Promise.all(
      imagesData.map(async (item, index) => {
        const data = await rodinPreprocessImage({
          generate_prompt: index === 0,
          image: item.compressedfile
        });
        const imageSrc = `data:image/png;base64,${data.data.processed_image}`;
        imagesData[index].preprocessImageSrc = imageSrc
        const response = await fetch(imageSrc);
        const processedBlob = await response.blob();
        const processedFile = new File([processedBlob], imagesData[index].name, { type: 'image/png' });
        imagesData[index].preprocessFile = processedFile
        return data.data;
      })
    );

    // 5. 提取第一张图片的特殊属性
    const firstImageData = preprocessResults[0] || {};
    const imageProperties = {
      prompt: firstImageData.prompt || '',
      allow_face_enhance: firstImageData.allow_face_enhance || false,
      default_face_enhance: firstImageData.default_face_enhance || false,
      height: firstImageData.height || 100,
      condition_mode: condition_mode,
      weightIndex: index
    };

    setIsRodinGenerating(true)

    genrateAreaRef.current.singleImgCroppingGenerate({
      imagesData,
      ...imageProperties
    })

  }

  const getScaleFactor = useCallback(() => {
    if (!canvasRef.current) return 1;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // 计算实际显示尺寸与画布尺寸的比例
    return canvas.width / (rect.width * devicePixelRatio);
  }, []);


  const cropAndDownloadBoxes = async () => {
    if (!imageRef.current || !boxes.length) return [];

    // 创建临时canvas用于裁剪
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    // 获取原图的实际尺寸
    const originalWidth = imageRef.current.naturalWidth;
    const originalHeight = imageRef.current.naturalHeight;

    // 计算缩放比例
    const scaleX = originalWidth / (canvasRef.current.width / devicePixelRatio);
    const scaleY = originalHeight / (canvasRef.current.height / devicePixelRatio);

    const croppedFiles = [];

    // 处理每个标注框
    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];

      // 计算实际图片上的裁剪位置
      const actualX = Math.floor(box.position.x * scaleX);
      const actualY = Math.floor(box.position.y * scaleY);
      const actualWidth = Math.floor(box.position.width * scaleX);
      const actualHeight = Math.floor(box.position.height * scaleY);

      // 设置临时canvas尺寸
      tempCanvas.width = actualWidth;
      tempCanvas.height = actualHeight;

      // 在临时canvas上裁剪图像
      tempCtx.drawImage(
        imageRef.current,
        actualX, actualY,
        actualWidth, actualHeight,
        0, 0,
        actualWidth, actualHeight
      );

      // 转换为blob并创建File对象
      const blob = await new Promise(resolve => {
        tempCanvas.toBlob(resolve, 'image/png');
      });

      // 创建File对象
      const file = new File([blob], `cropped_${i + 1}${Date.now()}.png`, { type: 'image/png' });
      croppedFiles.push(file);
    }

    return croppedFiles;
  };

  const handleGenerateFromFullImage = async () => {
    if (isDragging) return

    try {
      const alphaResult = await isHasAlpha2(initialData.imageFile);
      const imageData = {
        originFile: initialData.imageFile,
        name: initialData.name,
        isHasAlpha: alphaResult.isHasAlpha
      };
      const imageSrc = `data:image/png;base64,${initialData.processed_image}`;
      imageData.preprocessImageSrc = imageSrc;

      setIsGenerating(true)
      setCurrentStatus("GENERATING")
      setFullImageGenerating(true)
      const response = await fetch(imageSrc);
      const processedBlob = await response.blob();
      imageData.preprocessFile = new File([processedBlob], imageData.name, { type: 'image/png' });

      setIsRodinGenerating(true)
      genrateAreaRef.current.singleImgCroppingGenerate({
        imagesData: [imageData],
        prompt: initialData.prompt || '',
        allow_face_enhance: initialData.allow_face_enhance || false,
        default_face_enhance: initialData.default_face_enhance || false,
        height: initialData.height || 100,
        condition_mode: 'fuse',
        weightIndex: 1
      });
    } catch (error) {
      console.error('Error in image processing:', error);
      setIsGenerating(false)
      setFullImageGenerating(false)
      setCurrentStatus('GENERATE')
      tip({
        type: 'warning',
        content: "Image pre-processing failed.",
      });
      throw error;
    }
  }


  return (
    isOpen ?
      createPortal(<div className="fixed inset-0 z-[9999] flex flex-col gap-4 items-center justify-center bg-black/50 select-none" onClick={handleClose}>
        <div ref={canvasContainerRef} className={`rounded-[24px] p-2 xs:p-4 shadow-2xl flex gap-4 h-[fit-content] bg-[rgba(44,44,44,1)] backdrop-blur-[100px]
          ${useVerticalLayout ? 'flex-col justify-center items-center' : 'flex-row'}`}>
          <div ref={canvasScrollRef} className={`relative bg-[rgba(62,62,62,1)] flex-center overflow-hidden rounded-[16px] ${styles.canvasContainer} ${isGenerating ? 'pointer-events-none' : ''}`}>
            <div className={`w-full h-full text-white transition-300-ease bg-[rgba(0,0,0,0.3)] text-[20px]  pointer-events-none z-10 flex-center absolute-center
              ${styles['animate-breath-glow']}
              ${boxes?.length === 0 ? 'opacity-100' : 'opacity-0'}`}>
              Drag a box
            </div>
            <canvas
              ref={canvasRef}
              className={`w-full h-full cursor-crosshair ${styles.canvas}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleCanvasMouseMove}
            />
            {!isAnnotatorOpen?.isCrop && (
              <span onClick={handleGenerateFromFullImage} className={`cursor-pointer absolute bottom-[12px] left-[12px] rounded-[20px] text-[rgba(255,255,255,0.7)] [background:rgba(0,0,0,0.2)] [backdrop-filter:blur(20px)] p-[6px_12px] hover:bg-[rgba(0,0,0,0.3)] hover:text-white transition-300-ease
              ${isDragging ? 'pointer-events-none opacity-30' : ''}
              ${fullImageGenerating ? 'pointer-events-none opacity-60' : ''}
              `}>Generate from full image

              </span>
            )}
          </div>
          <div className={`flex  ${useVerticalLayout ? 'flex-row w-full' : 'flex-col w-[180px]'} ${isDragging ? 'pointer-events-none' : ''}`}>
            <div className={`flex ${useVerticalLayout ? 'space-y-0 gap-2 sm:gap-4 flex-row flex-1' : 'space-y-2 sm:space-y-2 flex-col flex-1'}`}>
              {boxes.map(box => {
                const bgColor = box.color.replace('1)', '0.2)');
                const isHovered = activeBoxState === box.id;
                return (
                  <div
                    key={box.id}
                    className={`group p-2 sm:p-3 rounded-[30px] transition-all flex items-center justify-center relative
                    ${useVerticalLayout ? 'w-[120px] xl:w-[180px]' : ''}
                    ${isDragging ? 'cursor-not-allowed' : ''}`}
                    style={{
                      backgroundColor: bgColor,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: box.color
                    }}
                    onMouseEnter={handleMouseEnterBtn.bind(null, box)}
                    onMouseLeave={handleMouseLeaveBtn}
                  >
                    <div className="flex items-center gap-2 ">
                      <div
                        className={`group/delete absolute-y-center  opacity-0 transition-300-ease w-5 h-5 cursor-pointer
                          ${box.id.startsWith('new-') ? 'left-[10px] 3xl:left-[30px]' : 'left-[20px] 3xl:left-[40px]'}
                          ${isHovered && !isGenerating ? 'opacity-100' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation(); // 防止冒泡
                          handleDeleteBox(box.id);
                        }}
                      >
                        <img src={deleteIcon} className="w-full h-full absolute group-hover/delete:hidden" alt="delete" />
                        <img src={deleteHoverIcon} className="w-full h-full absolute hidden group-hover/delete:block" alt="delete hover" />
                      </div>
                      <div className={`text-white font-medium relative transition-300-ease
                        ${isHovered && !isGenerating ? `${box.id.startsWith('new-') ? 'translate-x-[12px] 3xl:translate-x-[20px]' : 'translate-x-[12px] 3xl:translate-x-[20px]'}` : ''}`}>
                        {box.id.startsWith('new-') ? 'newObject' : box.name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* <div className={`${styles.confirmButton} ${isGenerating ? styles.isGenerating : ''}`}>
              <div className={styles.buttonContent}>
                {isGenerating ? (
                  <div className={`${styles.generating} relative left-[6px]`}>
                    Generating<span className={styles.dots}></span>
                  </div>
                ) : (
                  <>
                    <div className={styles.option} onClick={() => handlerClickWeight(0)}>
                      Multiview
                      <div className={styles.glow} />
                    </div>
                    <div className={styles.option} onClick={() => handlerClickWeight(1)}>
                      Fusion
                      <div className={styles.glow} />
                    </div>
                  </>
                )}
              </div>
            </div> */}


            <div
              className={`h-[50px] sm:h-[58px] ${useVerticalLayout ? 'w-[160px] xs:w-[180px]' : 'w-full mt-4'} ${generateBtnStyles.generateWrapper} ${currentStatus ? generateBtnStyles.gennerating : ""} ${isGenerating ? generateBtnStyles.isGenerating : ""}`}>


              <div className={`w-[257px] px-[20px] py-[12px] text-sm absolute top-0 left-1/2 -translate-x-1/2 translate-y-[calc(-100%-20px)] transition-300-ease bg-[rgba(255,255,255,0.1)] backdrop-blur-[60px] rounded-xl text-[rgba(255,255,255,0.6)] text-left ${multiImgTipBox ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                {t('RODIN_HEADER_UNLOCK_MULTI_TIPS_1')} <span className='text-white'>{t('RODIN_HEADER_UNLOCK_MULTI_TIPS_2')}</span> {t('RODIN_HEADER_UNLOCK_MULTI_TIPS_3')}. <span onClick={handleOpenSub} className='relative text-[#28e4f0] after:absolute after:[content:""] after:left-0 after:bottom-[-2px] after:transition-300-ease cursor-pointer after:bg-[#28e4f0] after:w-0 after:h-[1px] hover:after:w-full'>{t('RODIN_HEADER_UNLOCK_MULTI_TIPS_4')}!</span>
              </div>
              <div
                id='rodinGenerateBtn'
                onClick={handlerGenerate}
                className={`${generateBtnStyles.generateBtn} ${isGenerating ? generateBtnStyles.gennerating : generateBtnStyles.multi}`}>

                {
                  isAnnotatorOpen?.isCrop ? (
                    <span
                      className={`${generateBtnStyles.generateText} ${generateBtnStyles.GENERATE} text-white`}>
                      CONFIRM
                    </span>
                  ) :
                    isGenerating || ['GENERATE'].includes(currentStatus) ? (
                      <span
                        className={`${generateBtnStyles.generateText} ${generateBtnStyles.gennerating} ${['CONFIRM', 'GENERATE'].includes(currentStatus) ? generateBtnStyles.GENERATE : ""} ${isGenerating ? generateBtnStyles.generateDisabled : ""}`}>
                        {currentStatus ? StateMap[currentStatus] : t('BUTTON_GENERATE')}
                        {/* {isGenerating && <span className={styles.dots}></span>} */}
                      </span>
                    ) : (
                      <div className={`${generateBtnStyles.multiBox} absolute-center w-full h-full flex items-center [border-radius:inherit] overflow-hidden`}>
                        <div className='relative z-[1] peer/multi w-1/2 h-full flex-center transition-300-ease text-[rgba(255,255,255,0.6)] hover:text-white' onClick={handlerClickWeight.bind(null, 0)}>{t('BUTTON_MULTIVIEW')}</div>
                        <div className='relative z-[1] peer/fuse w-1/2 h-full flex-center transition-300-ease text-[rgba(255,255,255,0.6)] hover:text-white' onClick={handlerClickWeight.bind(null, 1)}>{t('BUTTON_FUSION')}</div>
                        <div className='absolute-y-center [will-change:left] [transform:translateZ(0)] z-0 left-[60%] peer-hover/multi:left-[10%] peer-hover/fuse:left-[60%] transition-300-ease [background:linear-gradient(180deg,#28E4F0_7.35%,#28B4F0_119.23%)] w-[56px] h-[12px] rounded-xl blur-[10px]'></div>
                      </div>
                    )
                }
                {['GENERATE'].includes(currentStatus) && (
                  <div className={`${generateBtnStyles.boxShadow}`}></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div >, document.body) : null
  );
};

export default ImageAnnotator;